import { useMutation } from '@tanstack/react-query';
import getConfig from 'next/config';

import { createBid } from 'Services/api/bids';
import { logger } from 'Services/logger/logger';
import { BID_TYPES } from 'Utilities/biddingState';
import { isAxiosError } from 'Utilities/isAxiosError';

import {
	BULK_BIDDING_ERROR_TYPES,
	getBiddingErrorReason,
	isCancelledAxiosError,
	isRepeatableError,
} from '../error.helpers';
import type { ActionableVehicle } from '../useFetchVehiclesForBulkBiddingCSV';

const { publicRuntimeConfig } = getConfig();

const { BULK_BIDDING_EXPONENTIAL_BACKOFF_MULTIPLIER, BULK_BIDDING_MAX_RETRY_ATTEMPTS } = publicRuntimeConfig;

export const MAX_RETRY_ATTEMPTS = Number(BULK_BIDDING_MAX_RETRY_ATTEMPTS ?? 3);
export const EXPONENTIAL_BACKOFF_MULTIPLIER = Number(BULK_BIDDING_EXPONENTIAL_BACKOFF_MULTIPLIER ?? 2000);

interface UseCreateBidMutationParams {
	setBidsProcessedCount: React.Dispatch<React.SetStateAction<number>>;
	signal: AbortSignal;
}

interface CreateBidParams {
	bidAmount: number;
	vehicle: ActionableVehicle['vehicle'];
}

export const useCreateBulkBidMutation = ({ setBidsProcessedCount, signal }: UseCreateBidMutationParams) => {
	return useMutation({
		mutationFn: async ({ bidAmount, vehicle }: CreateBidParams) => {
			if (signal.aborted) {
				return {
					bidAmount,
					reason: BULK_BIDDING_ERROR_TYPES.ABORTED,
					status: 'failure' as const,
					vehicle,
				};
			}

			try {
				const result = await createBid(
					{
						enquiryId: vehicle.id,
						type: BID_TYPES.PROXY,
						value: bidAmount,
					},
					signal,
				);

				return { bid: result.data, status: 'success' as const, vehicle };
			} catch (error) {
				// Don't retry requests that will always fail
				if (isAxiosError(error) && isRepeatableError(error)) {
					return {
						bidAmount,
						reason: getBiddingErrorReason(error),
						status: 'failure' as const,
						vehicle,
					};
				}

				if (isCancelledAxiosError(error)) {
					return {
						bidAmount,
						reason: BULK_BIDDING_ERROR_TYPES.ABORTED,
						status: 'failure' as const,
						vehicle,
					};
				}

				throw error;
			}
		},
		onError: (error: unknown, { bidAmount, vehicle }: CreateBidParams) => {
			logger.error({
				context: { bidAmount, enquiryId: vehicle.id },
				error: error instanceof Error ? error : new Error('Error creating bulk bid'),
				message: 'Failed to create bid in createBulkBids',
				scope: 'createBulkBids',
			});
		},
		onSettled() {
			setBidsProcessedCount((prev) => prev + 1);
		},
		retry: (failureCount) => {
			if (signal.aborted) {
				return false;
			}
			return failureCount < MAX_RETRY_ATTEMPTS;
		},
		retryDelay: (attemptIndex: number) => Math.min(EXPONENTIAL_BACKOFF_MULTIPLIER ** attemptIndex, 30000),
	});
};
